import {
  InflightNegotiations,
  NegotiableFieldOptional,
  Negotiation,
  NegotiationDetail,
  Severity,
  ValidationType,
  ValidationIssue,
  NegotiationStatus,
  NegotiationEvent,
  NegotiationType
} from 'api'
import { ColDef, GridApi, IRowNode } from 'ag-grid-community'
import { isNil } from 'lodash'
import { TypedValueGetterParams, ValidationCellRendererParams } from 'view/components/datagrid'
import {
  canAccept,
  canBackToStaging,
  canCancel,
  canPerformEvent,
  canRefresh,
  canReject,
  canSend,
  canWithdraw,
  isCounterable
} from '../shared/utils'
import { aggregateAllOrNothing, aggregateSum, hasMultipleValues } from 'utils'
import { NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { getNegotiatedValue } from 'model'

export const inflightNegotiationsRowDataGetter = (params: TypedValueGetterParams<InflightNegotiations>): NegotiationDetail[] | null => {
  const counterpartyId = decodeColIdValue(params?.colDef)

  if (!params.node?.group) {
    return getCounterpartyNegotiations(params?.node?.data ?? params?.data, counterpartyId)
  }

  return null
}

export interface AggregatedPartialNegotiationDetails {
  negotiationIds: string[]
  counterpartyId: string
  referenceRate?: NegotiationDetail['referenceRate']
  ownOrder?: boolean
  negotiation: {
    haircutType: Negotiation['haircutType']
    haircut: Negotiation['haircut']
    rate: Negotiation['rate']
    notional: number
    negotiationType: Negotiation['negotiationType']
    status: Negotiation['status']
  }
  validationIssues: ValidationIssue[]
}

/**
 * It creates a partial negotiation detail with the fields
 * we are using on grid view, some fields are changed from
 * negotiable fields to primitives as they are a sum
 **/
export const aggregatedPartialDetailRowDataGetter = (
  params: TypedValueGetterParams<InflightNegotiations>
): AggregatedPartialNegotiationDetails | null => {
  const negotiationDetails = inflightNegotiationsRowDataGetter(params) ?? []
  const negotiations = negotiationDetails.map(n => n.negotiation)
  const counterpartyId = decodeColIdValue(params?.colDef)

  if (!negotiations.length) return null

  return {
    negotiationIds: negotiations.map(n => n.negotiationId),
    counterpartyId,
    referenceRate: aggregateAllOrNothing(negotiationDetails, n => n.referenceRate),
    ownOrder: aggregateAllOrNothing(negotiationDetails, n => n.ownOrder),
    negotiation: {
      haircutType: aggregateNegotiableField(negotiations, n => n.haircutType),
      haircut: aggregateNegotiableField(negotiations, n => n.haircut),
      rate: aggregateNegotiableField(negotiations, n => n.rate),
      notional: aggregateSum(negotiations, n => getNegotiatedValue(n.notional)),
      negotiationType: aggregateAllOrNothing(negotiations, n => n.negotiationType, NegotiationType.INDIVIDUAL),
      status: aggregateAllOrNothing(negotiations, n => n.status, NegotiationStatus.REQUESTED_WITHOUT_PRICE)
    },
    validationIssues: aggregateValidationIssues(negotiationDetails)
  }
}

export const getCounterpartyNegotiations = (
  inflightNegotiations: InflightNegotiations | undefined,
  counterpartyId: string
): NegotiationDetail[] => {
  return inflightNegotiations?.negotiationsByCounterparty?.[counterpartyId] ?? []
}

export const getStatusValidationIssues = (params: ValidationCellRendererParams) => {
  const negotiations = inflightNegotiationsRowDataGetter(params as TypedValueGetterParams<InflightNegotiations>) ?? []
  if (negotiations.length) {
    const status = aggregateAllOrNothing(negotiations, n => n.negotiation.status)

    if (status === NegotiationStatus.EXPIRED) {
      return [
        {
          errorMessage: 'Expired.',
          fieldName: NegotiationFieldNames.STATUS,
          severity: Severity.ERROR,
          type: ValidationType.INVALID_VALUE
        }
      ]
    }

    if (status === NegotiationStatus.REJECTED) {
      return [
        {
          errorMessage: 'Rejected.',
          fieldName: NegotiationFieldNames.STATUS,
          severity: Severity.ERROR,
          type: ValidationType.INVALID_VALUE
        }
      ]
    }

    if (!status) {
      return [
        {
          errorMessage: 'Negotiations with different statuses.',
          fieldName: NegotiationFieldNames.STATUS,
          severity: Severity.WARN,
          type: ValidationType.INVALID_VALUE
        }
      ]
    }
  }

  return []
}

export const aggregateNegotiableField = <T>(
  negotiations: Negotiation[],
  getNegotiableField: (n: Negotiation) => null | NegotiableFieldOptional<T>
): NegotiableFieldOptional<T> => {
  return {
    initiatorLastValue: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.initiatorLastValue) ?? null,
    modified: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.modified) ?? false,
    modifiedValue: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.modifiedValue) ?? null,
    negotiated: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.negotiated) ?? null,
    original: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.original) ?? null,
    responderLastValue: aggregateAllOrNothing(negotiations, n => getNegotiableField(n)?.responderLastValue) ?? null
  }
}

export const aggregateValidationIssues = (negotiationDetails: NegotiationDetail[]): ValidationIssue[] => {
  return [...new Set(negotiationDetails.flatMap(n => n.validationIssues))].filter(v => v.type !== ValidationType.MISSING_MANDATORY)
}

export const decodeColIdValue = (colDef: ColDef | undefined | null) => {
  const [value] = colDef?.colId?.split('--') ?? []

  return value ?? ''
}

export const encodeColIdValue = (colId: string, value: string | number) => {
  return `${value}--${colId}`
}

export const isNotExpiredOrRejected = (detail: NegotiationDetail) => {
  return ![NegotiationStatus.EXPIRED, NegotiationStatus.REJECTED].includes(detail.negotiation.status)
}

export const isFirstCollateralRow = (params: { node: IRowNode<InflightNegotiations>; api: GridApi }) => {
  const rowIndex = params.node?.rowIndex

  const prevRow = !isNil(rowIndex) && rowIndex > 0 ? params?.api?.getDisplayedRowAtIndex(rowIndex - 1) : undefined

  if (prevRow?.data?.collateral?.id === params?.node?.data?.collateral?.id) {
    return false
  }

  return true
}

export const canUpdateGridViewRate = (details: NegotiationDetail[]) => {
  return (
    details.every(d => isCounterable(d.negotiation.status) && canPerformEvent(d, NegotiationEvent.UPDATE_ATTRIBUTES)) &&
    !hasMultipleValues(details, n => n.negotiation.status) &&
    !hasMultipleValues(details, n => getNegotiatedValue(n.negotiation.rate))
  )
}

export const canUpdateGridViewHaircut = (details: NegotiationDetail[]) => {
  return (
    details.every(d => isCounterable(d.negotiation.status) && canPerformEvent(d, NegotiationEvent.UPDATE_ATTRIBUTES)) &&
    !hasMultipleValues(details, n => n.negotiation.status) &&
    !hasMultipleValues(details, n => getNegotiatedValue(n.negotiation.haircut))
  )
}

export const isInflight = (candidate: AggregatedPartialNegotiationDetails | InflightNegotiations): candidate is InflightNegotiations => {
  return !!(
    'collateral' in candidate &&
    'direction' in candidate &&
    'target' in candidate &&
    'inflight' in candidate &&
    'negotiationsByCounterparty' in candidate
  )
}

export const isAggregatedDetail = (candidate: any): candidate is AggregatedPartialNegotiationDetails => {
  return !!('negotiationIds' in candidate && 'negotiation' in candidate)
}

export const canAcceptGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canAccept)
}

export const canSendGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canSend)
}

export const canRefreshGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canRefresh)
}

export const canWithdrawGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canWithdraw)
}

export const canBackToStagingGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canBackToStaging)
}

export const canRejectGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canReject)
}

export const canCancelGridView = (detail: NegotiationDetail, _index: number, details: NegotiationDetail[]) => {
  return getAllRelatedNegotiations(detail, details).every(canCancel)
}

const getAllRelatedNegotiations = (detail: NegotiationDetail, details: NegotiationDetail[]) => {
  return details.filter(d => {
    return (
      d.counterparty?.id === detail.counterparty?.id &&
      d.collateral?.id === detail.collateral?.id &&
      d.negotiation.direction === detail.negotiation.direction
    )
  })
}
